// ExerciseViewer.tsx — a single toolkit exercise as a gentle stepper:
//   intro (what you'll do)  →  steps (do / journal / action / safety / media)  →  done
// App-styled, the app's own SVG icons (never emoji), no em-dashes in copy. Journal
// prompts render as read-only cards that point the user to their own journal
// (in-app journaling is off). Mounted full-screen as an app overlay.
import React from 'react';
import { Btn, Body, Display, Pressable } from '@/components/ui';
import { Icon, type IconName } from '@/components/icons';
import { TOOL_THEME, type Tool, type ToolStep } from '@/data/toolkit';
import { NEW_UI } from '@/config/uiFlags';
import { TRIAGE_V2 } from '@/config/features';
import { BackButton } from '@/components/BackButton';

type View = 'intro' | 'step' | 'done';

// NEW_UI: re-skin the whole exercise to the Hold-page palette (off-white canvas,
// flat white cards) by overriding the CSS variables the component already uses. One
// place, every surface follows. Live (flag off) keeps the warm cream/beige styling.
const NEW_SKIN = (NEW_UI
  ? {
      '--cream': '#F7F5F2',
      '--card-top': '#FFFFFF',
      '--card2-top': '#FFFFFF',
      '--card2-bot': '#FFFFFF',
      '--hair': 'rgba(0,0,0,0.06)',
      '--glowwarm': 'rgba(185,130,50,0.05)',
    }
  : {}) as React.CSSProperties;

import { ReviewCommentButton } from '@/flows/review/ReviewCommentButton';

export function ExerciseViewer({ tool, onClose }: { tool: Tool; onClose: () => void }) {
  const [view, setView] = React.useState<View>('intro');
  const [step, setStep] = React.useState(0);
  const theme = TOOL_THEME[tool.theme];
  const journalCount = tool.steps.filter((s) => s.journal).length;

  // Review Mode: a comment target for EVERY toolkit page (intro, each step, done),
  // keyed so the export maps back to the exact page. Renders nothing unless review
  // mode is on, so end users never see it.
  const reviewTarget =
    view === 'step'
      ? { id: `${tool.id}:s${step}`, label: `${tool.title} — ${tool.steps[step]?.title || `Step ${step + 1}`}` }
      : view === 'done'
        ? { id: `${tool.id}:done`, label: `${tool.title} — Complete` }
        : { id: tool.id, label: tool.title };

  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => { scrollRef.current?.scrollTo(0, 0); }, [view, step]);

  const close = () => onClose();
  const back = () => {
    if (view === 'step' && step > 0) setStep(step - 1);
    else if (view === 'step') setView('intro');
    else if (view === 'done') setView('intro');
    else close();
  };

  return (
    <div style={{ ...NEW_SKIN, position: 'absolute', inset: 0, zIndex: 84, background: 'var(--cream)', display: 'flex', flexDirection: 'column', animation: 'rise .42s cubic-bezier(.2,.7,.3,1)' }}>
      <ReviewCommentButton targetId={reviewTarget.id} kind="step" label={reviewTarget.label} />
      {/* top bar — circular, raised buttons */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 'calc(14px + var(--safe-top)) 18px 12px', background: 'var(--cream)' }}>
        {TRIAGE_V2 ? <BackButton onClick={back} /> : <Pressable as="button" onClick={back} ariaLabel="Back" style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--edge), var(--sh1)' }}>
          <Icon name="chevLeft" size={19} />
        </Pressable>}
        <span style={{ flex: 1, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 17, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {view === 'step' ? `${step + 1} of ${tool.steps.length}` : view === 'done' ? 'Complete' : 'Exercise'}
        </span>
        {!TRIAGE_V2 && <Pressable as="button" onClick={close} ariaLabel="Close" style={{ width: 44, height: 44, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)', color: 'var(--ink-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: 'var(--edge), var(--sh1)' }}>
          <Icon name="chevDown" size={19} />
        </Pressable>}
      </div>

      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', padding: '18px 18px calc(28px + var(--safe-bottom))' }}>
        {view === 'intro' && (
          <div className="fade-up">
            {/* warm hero card with a soft top-right glow */}
            <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 22, padding: '22px 22px 24px', marginBottom: 18, background: 'linear-gradient(180deg, var(--card2-top), var(--card2-bot))', border: '1px solid var(--hair)', boxShadow: 'var(--edge), var(--sh1)' }}>
              <span aria-hidden="true" style={{ position: 'absolute', top: -44, right: -34, width: 190, height: 190, borderRadius: 999, background: 'radial-gradient(circle, var(--glowwarm), transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber-deep)', marginBottom: 10 }}>
                {tool.group}
              </div>
              <div style={{ position: 'relative', fontFamily: 'Fraunces, Georgia, serif', fontSize: 32, fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.01em', color: 'var(--ink)' }}>
                {tool.title}
              </div>
              <p style={{ position: 'relative', margin: '12px 0 0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, lineHeight: 1.55, color: 'var(--ink-soft)', maxWidth: '34ch' }}>{tool.blurb}</p>
            </div>

            {/* divided meta row (no pills) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, padding: '2px 4px', marginBottom: 22 }}>
              <Stat icon="clock" color="var(--amber-deep)" value={tool.time} />
              <MetaDivider />
              <Stat icon="leaf" color="var(--amber-deep)" value={String(tool.steps.length)} label="steps" />
              {journalCount > 0 && <><MetaDivider /><Stat icon="pencil" color="var(--green-deep)" value={String(journalCount)} label="journal" /></>}
            </div>

            <div style={{ ...eyebrow, margin: '0 2px 10px' }}>What you'll do</div>
            {/* connected numbered timeline, inside a white card */}
            <div style={{ background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 18, boxShadow: 'var(--edge), var(--sh1)', padding: '4px 16px 4px 8px', marginBottom: 8 }}>
              {tool.steps.map((s, i) => (
                <TimelineRow
                  key={i}
                  index={i}
                  total={tool.steps.length}
                  title={s.title}
                  journal={!!s.journal}
                  current={i === 0}
                  // TRIAGE_V2: the list is a table of contents, not shortcuts into the middle of the exercise.
                  onClick={TRIAGE_V2 ? undefined : () => { setStep(i); setView('step'); }}
                />
              ))}
            </div>
          </div>
        )}

        {view === 'step' && (
          <StepBody key={step} tool={tool} s={tool.steps[step]} index={step} theme={theme}
            last={step === tool.steps.length - 1}
            onPrev={step > 0 ? () => setStep(step - 1) : undefined}
            onNext={() => (step === tool.steps.length - 1 ? setView('done') : setStep(step + 1))} />
        )}

        {view === 'done' && (
          <div className="fade-up" style={{ textAlign: 'center', padding: '30px 10px' }}>
            <span style={{ display: 'inline-flex', width: 70, height: 70, borderRadius: 999, alignItems: 'center', justifyContent: 'center', color: '#fff', background: theme.bg, marginBottom: 16 }}>
              <Icon name="check" size={32} sw={2.4} />
            </span>
            <Display parts={{ pre: 'Well ', em: 'done', post: '.' }} size={24} />
            <Body dim size={14.5} style={{ marginTop: 10, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
              You turned up for yourself today. Come back to <b>{tool.title}</b> any time you need it. Repetition is how it works.
            </Body>
            <div style={{ maxWidth: 280, margin: '22px auto 0' }}>
              <Btn full size="lg" onClick={close}>Back to toolkits</Btn>
            </div>
          </div>
        )}
      </div>

      {/* pinned CTA for the intro — the list scrolls behind it */}
      {view === 'intro' && (
        <div style={{ padding: '12px 18px calc(16px + var(--safe-bottom))', background: 'var(--cream)', borderTop: '1px solid var(--hair)' }}>
          <Btn full size="lg" onClick={() => { setStep(0); setView('step'); }} leading={<Icon name="sparkle" size={18} />}>
            {tool.beginLabel || 'Begin'}
          </Btn>
        </div>
      )}
    </div>
  );
}

// One inline stat in the divided meta row: amber/green icon, bold value, soft label.
function Stat({ icon, value, label, color }: { icon: IconName; value: string; label?: string; color: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
      <span style={{ color, display: 'inline-flex' }}><Icon name={icon} size={16} /></span>
      <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>{value}</span>
      {label && <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, color: 'var(--ink-soft)' }}>{label}</span>}
    </span>
  );
}
function MetaDivider() {
  return <span aria-hidden="true" style={{ width: 1, height: 18, background: 'var(--hair-strong, rgba(44,42,37,0.16))', margin: '0 14px' }} />;
}

// One row in the intro's connected numbered timeline. The vertical connector runs
// behind the circles (opaque, so it reads as a line linking the steps).
function TimelineRow({ index, total, title, journal, current, onClick }: {
  index: number; total: number; title: string; journal: boolean; current: boolean; onClick?: () => void;
}) {
  const isFirst = index === 0;
  const isLast = index === total - 1;
  // No onClick = read-only contents row (div, no chevron).
  const Row = (onClick ? 'button' : 'div') as 'button';
  return (
    <Row
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      style={{
        display: 'flex', alignItems: 'stretch', gap: 4, width: '100%', textAlign: 'left',
        background: 'transparent', border: 'none', cursor: onClick ? 'pointer' : 'default', padding: 0,
      }}
    >
      {/* connector + numbered circle */}
      <span style={{ position: 'relative', width: 52, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <span aria-hidden="true" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', width: 2, background: 'var(--hair)', top: isFirst ? 24 : 0, bottom: isLast ? 'calc(100% - 24px)' : 0 }} />
        <span style={{
          position: 'relative', zIndex: 1, marginTop: 14, width: 34, height: 34, borderRadius: 999, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'var(--cream)',
          border: current ? '2px solid var(--amber-deep)' : '1px solid var(--hair-strong, rgba(44,42,37,0.16))',
          color: current ? 'var(--amber-deep)' : 'var(--ink-soft)',
          fontFamily: 'Fraunces, Georgia, serif', fontSize: 16, fontWeight: current ? 600 : 400,
        }}>{index + 1}</span>
      </span>
      {/* title + journal tag + chevron */}
      <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '14px 4px 14px 0', borderBottom: isLast ? 'none' : '1px solid var(--hair)' }}>
        <span style={{ flex: 1, minWidth: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 17, fontWeight: 600, lineHeight: 1.25, color: 'var(--ink)' }}>{title}</span>
        {journal && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--green-deep)', background: 'rgba(29,158,117,0.12)', padding: '4px 10px', borderRadius: 999 }}>
            <Icon name="pencil" size={12} /> journal
          </span>
        )}
        {onClick && <span style={{ color: 'var(--ink-soft)', opacity: 0.45, flexShrink: 0, display: 'inline-flex' }}><Icon name="chevRight" size={16} /></span>}
      </span>
    </Row>
  );
}

const eyebrow: React.CSSProperties = { fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', margin: '4px 2px 0' };

function StepBody({ tool, s, index, theme, last, onPrev, onNext }: {
  tool: Tool; s: ToolStep; index: number; theme: { bg: string; soft: string; fg: string };
  last: boolean; onPrev?: () => void; onNext: () => void;
}) {
  return (
    <div className="fade-up">
      {/* progress */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {tool.steps.map((_, k) => (
          <span key={k} style={{ height: 5, borderRadius: 3, flex: 1, background: k <= index ? theme.fg : 'var(--hair)' }} />
        ))}
      </div>
      {s.kicker && <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.fg, marginBottom: 5 }}>{s.kicker}</div>}
      <Display parts={{ pre: s.title, em: '' }} size={23} lh={1.25} style={{ marginBottom: 14 }} />

      {s.body?.map((p, i) => (
        <p key={i} style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, lineHeight: 1.6, color: 'var(--ink)', margin: '0 0 14px' }}>{p}</p>
      ))}

      {s.list && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
          {s.list.map((li, i) => (
            <li key={i} style={{ position: 'relative', padding: '8px 0 8px 26px', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink)', borderBottom: i === s.list!.length - 1 ? 'none' : '1px solid var(--hair)' }}>
              <span aria-hidden="true" style={{ position: 'absolute', left: 6, top: s.check ? 10 : 7, color: theme.fg, fontWeight: 700 }}>{s.check ? '○' : '›'}</span>
              {li}
            </li>
          ))}
        </ul>
      )}

      {s.kind === 'media' && s.media && (
        <div style={{ borderRadius: 16, padding: 24, textAlign: 'center', margin: '0 0 18px', background: theme.soft, color: theme.fg }}>
          <span style={{ display: 'inline-flex', width: 46, height: 46, borderRadius: 999, alignItems: 'center', justifyContent: 'center', background: theme.bg, color: '#fff' }}>
            <Icon name="play" size={18} />
          </span>
          <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600, marginTop: 8 }}>{s.media}</div>
        </div>
      )}

      {s.safety && (
        <Block tone="safety" label={s.safetyLabel || 'Your safety comes first'} icon="shield" items={s.safety} hint={s.shint} marker="circle" />
      )}
      {s.action && (
        <Block tone="action" label={s.actionLabel || 'What to do now'} icon="check" items={s.action} hint={s.ahint} fg={theme.fg} soft={theme.soft} marker="circle" />
      )}
      {s.examples && (
        <ExamplesBlock examples={s.examples} fg={theme.fg} soft={theme.soft} />
      )}
      {s.journal && (
        <Block tone="journal" label={s.journalLabel || 'In your journal'} icon="pencil" items={s.journal} hint={s.jhint} marker="none" />
      )}

      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        {onPrev && <Btn variant="ghost" onClick={onPrev} style={{ flex: 1 }}>Back</Btn>}
        <Btn onClick={onNext} style={{ flex: onPrev ? 1 : 1 }} leading={last ? <Icon name="check" size={17} /> : undefined}>
          {last ? 'Finish' : 'Next'}
        </Btn>
      </div>
    </div>
  );
}

// A collapsible bank of worked examples, grouped by situation. Top-level toggle
// keeps the step clean; tapping a group reveals its wordings (with optional tone
// chip + one-line "why this works"). Used by the boundary tool.
function ExamplesBlock({ examples, fg }: {
  examples: NonNullable<ToolStep['examples']>; fg: string; soft: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [groups, setGroups] = React.useState<Set<number>>(() => new Set());
  const toggleGroup = (i: number) =>
    setGroups((prev) => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const toneChip: Record<string, { bg: string; fg: string; label: string }> = {
    soft: { bg: 'rgba(29,158,117,0.12)', fg: 'var(--green-deep)', label: 'gentle' },
    neutral: { bg: 'rgba(74,52,24,0.07)', fg: 'var(--ink-soft)', label: 'plain' },
    firm: { bg: 'rgba(176,65,63,0.10)', fg: '#9C3D2C', label: 'firm' },
  };
  return (
    <div style={{ margin: '6px 0 18px' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: 9, width: '100%',
          background: 'var(--card-top)', border: '1px solid var(--hair)', borderRadius: 14,
          padding: '12px 14px', cursor: 'pointer', textAlign: 'left',
          fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, fontWeight: 600, color: fg,
        }}
      >
        <Icon name="sparkle" size={15} />
        <span style={{ flex: 1 }}>{examples.label || 'See examples'}</span>
        <span style={{ display: 'inline-flex', transition: 'transform .15s ease', transform: open ? 'none' : 'rotate(-90deg)' }}>
          <Icon name="chevDown" size={16} />
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {examples.groups.map((g, gi) => {
            const gOpen = groups.has(gi);
            return (
              <div key={gi} style={{ border: '1px solid var(--hair)', borderRadius: 13, overflow: 'hidden', background: 'var(--cream)' }}>
                <button
                  type="button"
                  onClick={() => toggleGroup(gi)}
                  aria-expanded={gOpen}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 9, width: '100%',
                    background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                    padding: '11px 13px', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 600, color: 'var(--ink)',
                  }}
                >
                  <span style={{ flex: 1 }}>{g.title}</span>
                  <span style={{ display: 'inline-flex', color: 'var(--ink-soft)', transition: 'transform .15s ease', transform: gOpen ? 'none' : 'rotate(-90deg)' }}>
                    <Icon name="chevDown" size={15} />
                  </span>
                </button>
                {gOpen && (
                  <div style={{ padding: '0 13px 11px' }}>
                    {g.items.map((it, ii) => (
                      <div key={ii} style={{ padding: '9px 0', borderTop: '1px solid var(--hair)' }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                          {it.tone && (
                            <span style={{ flexShrink: 0, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 999, background: toneChip[it.tone].bg, color: toneChip[it.tone].fg }}>
                              {toneChip[it.tone].label}
                            </span>
                          )}
                          <span style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 15, lineHeight: 1.4, color: 'var(--ink)' }}>{it.text}</span>
                        </div>
                        {it.why && (
                          <p style={{ margin: '5px 0 0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, lineHeight: 1.5, fontStyle: 'italic', color: 'var(--ink-soft)' }}>{it.why}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// A coloured prompt block (journal = lavender, action = green/theme, safety = red).
function Block({ tone, label, icon, items, hint, fg, soft, marker }: {
  tone: 'journal' | 'action' | 'safety'; label: string; icon: IconName; items: string[]; hint?: string;
  fg?: string; soft?: string; marker: 'circle' | 'none';
}) {
  const palette =
    tone === 'safety'
      ? { bg: '#FBECEC', border: '#E9C4C4', head: '#B0413F', text: '#8A3331', line: '#F0D6D6' }
      : tone === 'action'
        ? { bg: soft || 'rgba(29,158,117,0.08)', border: 'rgba(29,158,117,0.22)', head: fg || 'var(--green-deep)', text: 'var(--ink)', line: 'rgba(29,158,117,0.18)' }
        : { bg: 'rgba(106,92,176,0.07)', border: 'rgba(106,92,176,0.20)', head: '#5a4ea0', text: '#3f3a52', line: 'rgba(106,92,176,0.16)' };
  return (
    <div style={{ background: palette.bg, border: `1px solid ${palette.border}`, borderRadius: 16, padding: '15px 16px', margin: '6px 0 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, fontWeight: 700, color: palette.head, textTransform: tone === 'safety' ? 'uppercase' : 'none', letterSpacing: tone === 'safety' ? '0.04em' : 0 }}>
        <Icon name={icon} size={15} /> {label}
      </div>
      {items.map((p, i) => (
        <div key={i} style={{ position: 'relative', padding: marker === 'circle' ? '7px 0 7px 22px' : '7px 0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14.5, lineHeight: 1.5, color: palette.text, borderBottom: i === items.length - 1 ? 'none' : `1px solid ${palette.line}` }}>
          {marker === 'circle' && <span aria-hidden="true" style={{ position: 'absolute', left: 4, top: 8, color: palette.head, fontSize: 13 }}>○</span>}
          {p}
        </div>
      ))}
      {hint && <p style={{ margin: '10px 0 0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12.5, fontStyle: 'italic', color: palette.head }}>{hint}</p>}
    </div>
  );
}
